import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../global/supabase';

const statusConfig = {
  matched: { label: 'Matched', color: '#388e3c', bg: '#e8f5e9', icon: '\u2713' },
  pending: { label: 'Pending', color: '#f57c00', bg: '#fff3e0', icon: '\u25CF' },
  unmatched: { label: 'Unmatched', color: '#d32f2f', bg: '#ffebee', icon: '!' },
  timeout: { label: 'Timeout', color: '#666', bg: '#f5f5f5', icon: '\u2014' },
};

const confidenceColors = {
  high: '#388e3c',
  medium: '#f57c00',
  low: '#ff9800',
};

const RANGE_OPTIONS = [
  { label: 'Last 24h', value: 1 },
  { label: 'Last 7d', value: 7 },
  { label: 'Last 30d', value: 30 },
];

function StationChangeAlerts({ userInfo }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState(7);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const since = new Date(Date.now() - dateRange * 24 * 60 * 60 * 1000).toISOString();

      let query = supabase
        .from('station_change_alerts')
        .select('*')
        .eq('deployment_id', 'mayo')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(20);

      if (statusFilter !== 'all') {
        query = query.eq('root_cause_status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching alerts:', error);
        return;
      }
      setAlerts(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateRange]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>Station Change Alerts</h2>
        <div style={styles.headerControls}>
          {/* Date Range Filter */}
          <div style={styles.rangeGroup}>
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setDateRange(opt.value); setLoading(true); }}
                style={dateRange === opt.value ? styles.filterActive : styles.filterBtn}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Status Filter */}
      <div style={styles.filterRow}>
        {['all', 'pending', 'matched', 'unmatched'].map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setLoading(true); }}
            style={statusFilter === s ? styles.filterActive : styles.filterBtn}
          >
            {s === 'all' ? 'All' : statusConfig[s]?.label || s}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Loading alerts...
        </div>
      ) : alerts.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No station change alerts found for the selected filters.
        </div>
      ) : (
        <div style={{ padding: '0 16px 16px' }}>
          {alerts.map((alert) => {
            const config = statusConfig[alert.root_cause_status] || statusConfig.pending;
            const isExpanded = expandedId === alert.id;
            const matches = alert.root_cause_matches || [];
            const bestMatch = matches[0];

            return (
              <div
                key={alert.id}
                style={{
                  ...styles.alertCard,
                  borderLeftColor: config.color,
                }}
              >
                {/* Collapsed View */}
                <div
                  style={styles.alertHeader}
                  onClick={() => toggleExpand(alert.id)}
                >
                  <div style={styles.alertMain}>
                    <span style={styles.stationLabel}>{alert.station_label}</span>
                    <span style={styles.medLabel}>{alert.medication_label}</span>
                    <span
                      style={{
                        ...styles.changeAmount,
                        color: alert.change_amount > 0 ? '#388e3c' : '#d32f2f',
                      }}
                    >
                      {alert.change_amount > 0 ? '+' : ''}{alert.change_amount}
                    </span>
                  </div>
                  <div style={styles.alertMeta}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        background: config.bg,
                        color: config.color,
                        border: `1px solid ${config.color}`,
                      }}
                    >
                      {alert.root_cause_status === 'pending' && (
                        <span style={styles.pulse} />
                      )}
                      {config.icon} {config.label}
                    </span>
                    <span style={styles.timestamp}>
                      {new Date(alert.trigger_timestamp).toLocaleString()}
                    </span>
                    {alert.trigger_record_personnel && (
                      <span style={styles.personnel}>{alert.trigger_record_personnel}</span>
                    )}
                    <span style={styles.chevron}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
                  </div>
                </div>

                {/* Expanded View */}
                {isExpanded && (
                  <div style={styles.expandedContent}>
                    <div style={styles.detailGrid}>
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Previous Qty</span>
                        <span style={styles.detailValue}>{alert.previous_quantity ?? 'N/A'}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>New Qty</span>
                        <span style={styles.detailValue}>{alert.new_quantity ?? 'N/A'}</span>
                      </div>
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Change</span>
                        <span
                          style={{
                            ...styles.detailValue,
                            color: alert.change_amount > 0 ? '#388e3c' : '#d32f2f',
                            fontWeight: '700',
                          }}
                        >
                          {alert.change_amount > 0 ? '+' : ''}{alert.change_amount}
                        </span>
                      </div>
                      <div style={styles.detailItem}>
                        <span style={styles.detailLabel}>Reason</span>
                        <span style={styles.detailValue}>{alert.trigger_record_reason || 'Not specified'}</span>
                      </div>
                    </div>

                    {/* Root Cause Section */}
                    {alert.root_cause_status === 'matched' && bestMatch && (
                      <div style={styles.matchBox}>
                        <h4 style={{ margin: '0 0 8px', color: '#388e3c', fontSize: '14px' }}>
                          Root Cause Match
                        </h4>
                        <div style={styles.matchGrid}>
                          <div>
                            <span style={styles.matchLabel}>Matching Unit</span>
                            <span style={styles.matchValue}>{bestMatch.unit_label}</span>
                          </div>
                          <div>
                            <span style={styles.matchLabel}>Personnel</span>
                            <span style={styles.matchValue}>{bestMatch.personnel || 'Unknown'}</span>
                          </div>
                          <div>
                            <span style={styles.matchLabel}>Reason</span>
                            <span style={styles.matchValue}>{bestMatch.reason || 'Not specified'}</span>
                          </div>
                          <div>
                            <span style={styles.matchLabel}>Timestamp</span>
                            <span style={styles.matchValue}>
                              {new Date(bestMatch.record_timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span style={styles.matchLabel}>Qty Change</span>
                            <span style={styles.matchValue}>
                              {bestMatch.quantity_change > 0 ? '+' : ''}{bestMatch.quantity_change}
                            </span>
                          </div>
                          <div>
                            <span style={styles.matchLabel}>Confidence</span>
                            <span
                              style={{
                                ...styles.confidenceBadge,
                                background: confidenceColors[bestMatch.confidence] || '#999',
                              }}
                            >
                              {bestMatch.confidence}
                            </span>
                          </div>
                        </div>

                        {/* Timeline */}
                        <div style={styles.timeline}>
                          <div style={styles.timelineItem}>
                            <div style={{ ...styles.timelineDot, background: config.color }} />
                            <span style={{ fontSize: '12px', color: '#555' }}>
                              Station change: {new Date(alert.trigger_timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div style={styles.timelineLine} />
                          <div style={styles.timelineItem}>
                            <div style={{ ...styles.timelineDot, background: '#388e3c' }} />
                            <span style={{ fontSize: '12px', color: '#555' }}>
                              Matched record: {new Date(bestMatch.record_timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#888', marginLeft: '20px', marginTop: '4px' }}>
                            Time delta: {formatTimeDelta(alert.trigger_timestamp, bestMatch.record_timestamp)}
                          </div>
                        </div>
                      </div>
                    )}

                    {(alert.root_cause_status === 'unmatched' || alert.root_cause_status === 'timeout') && (
                      <div style={styles.unmatchedBox}>
                        <strong style={{ color: '#d32f2f' }}>No corresponding change found across other units.</strong>
                        <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
                          Manual review recommended.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatTimeDelta(ts1, ts2) {
  const diff = Math.abs(new Date(ts1).getTime() - new Date(ts2).getTime());
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${remainingMins}m`;
  }
  return `${minutes}m`;
}

const styles = {
  container: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    marginBottom: '24px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '20px',
    borderBottom: '1px solid #eee',
  },
  headerTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#333',
    fontWeight: '600',
  },
  headerControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  filterRow: {
    display: 'flex',
    gap: '6px',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    flexWrap: 'wrap',
  },
  filterBtn: {
    padding: '6px 14px',
    background: '#f5f5f5',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
  filterActive: {
    padding: '6px 14px',
    background: '#0E3293',
    color: '#fff',
    border: '1px solid #0E3293',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  rangeGroup: {
    display: 'flex',
    gap: '4px',
  },
  alertCard: {
    borderLeft: '4px solid #999',
    borderRadius: '0 8px 8px 0',
    marginBottom: '8px',
    background: '#fafafa',
    overflow: 'hidden',
  },
  alertHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    flexWrap: 'wrap',
    gap: '8px',
  },
  alertMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  stationLabel: {
    fontWeight: '600',
    color: '#333',
    fontSize: '14px',
  },
  medLabel: {
    color: '#555',
    fontSize: '13px',
  },
  changeAmount: {
    fontWeight: '700',
    fontSize: '16px',
  },
  alertMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  statusBadge: {
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  pulse: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#f57c00',
    animation: 'pulse 1.5s infinite',
  },
  timestamp: {
    fontSize: '12px',
    color: '#888',
  },
  personnel: {
    fontSize: '12px',
    color: '#555',
    fontWeight: '500',
  },
  chevron: {
    fontSize: '10px',
    color: '#999',
  },
  expandedContent: {
    padding: '0 16px 16px',
    borderTop: '1px solid #eee',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    padding: '12px 0',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  detailLabel: {
    fontSize: '11px',
    color: '#888',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#333',
  },
  matchBox: {
    background: '#e8f5e9',
    border: '1px solid #c8e6c9',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '8px',
  },
  matchGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '8px',
  },
  matchLabel: {
    display: 'block',
    fontSize: '11px',
    color: '#666',
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  matchValue: {
    display: 'block',
    fontSize: '13px',
    color: '#333',
  },
  confidenceBadge: {
    display: 'inline-block',
    color: '#fff',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  timeline: {
    marginTop: '12px',
    paddingTop: '8px',
    borderTop: '1px solid #c8e6c9',
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  timelineDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  timelineLine: {
    width: '2px',
    height: '12px',
    background: '#c8e6c9',
    marginLeft: '3px',
  },
  unmatchedBox: {
    background: '#ffebee',
    border: '1px solid #ffcdd2',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '8px',
  },
};

export default StationChangeAlerts;
